import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import '../Styling/Amazon_BC_inv_item_errorScreen.css';
import { Base_URL } from '../url';

const ITEMS_PER_PAGE = 50;

const Amazon_BC_inv_item_errorScreen = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('asc');

  /* ---------- Filters ---------- */
  const [selectedCreatedDate, setSelectedCreatedDate] = useState('');
  const [selectedErrorId, setSelectedErrorId] = useState('');
  const [selectedExDocNo, setSelectedExDocNo] = useState('');

  const API_URL = `${Base_URL}/Amazon_Screen_Request/GetAmazonBcInvItemErrors`;

  useEffect(() => {
    fetchErrorData();
  }, []);

  const fetchErrorData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(API_URL, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000,
      });
      console.log(res.data);
      const apiData = Array.isArray(res.data?.data) ? res.data.data : [];
      setData(apiData);
      setFilteredData(apiData);
      setCurrentPage(1);
    } catch (err) {
      setError(err.message === 'timeout of 10000ms exceeded' 
        ? 'Request timed out. Please try again.' 
        : err.response?.data?.message || err.message || 'Failed to load item errors');
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Immediate Filtering Logic ---------- */
  useEffect(() => {
    setFiltering(true);
    const timer = setTimeout(() => {
      let temp = [...data];
      if (selectedCreatedDate) {
        temp = temp.filter((x) => new Date(x.createdDate).toISOString().slice(0, 10) === selectedCreatedDate);
      }
      if (selectedErrorId) {
        temp = temp.filter((x) => String(x.errorId) === selectedErrorId);
      }
      if (selectedExDocNo) {
        temp = temp.filter((x) => x.externalDocumentNumber === selectedExDocNo);
      }
      setFilteredData(temp);
      setCurrentPage(1);
      setFiltering(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [selectedCreatedDate, selectedErrorId, selectedExDocNo, data]);

  const clearFilters = () => {
    setSelectedCreatedDate('');
    setSelectedErrorId('');
    setSelectedExDocNo('');
  };

  const parseErrorDesc = (errorDesc) => {
    if (!errorDesc) return '-';
    
    try {
      // Try to parse as JSON
      const parsed = JSON.parse(errorDesc);
      
      // Check if it has nested error object
      if (parsed.error && parsed.error.code && parsed.error.message) {
        return {
          code: parsed.error.code,
          message: parsed.error.message
        };
      }
      
      // Check if it has direct code and message
      if (parsed.code && parsed.message) {
        return {
          code: parsed.code,
          message: parsed.message
        };
      }
      
      // If JSON but no recognized structure, return original
      return errorDesc;
    } catch (e) {
      // Not JSON, return as-is
      return errorDesc;
    }
  };

  /* ---------- Excel Download Handler ---------- */
  const handleDownloadExcel = () => {
    if (filteredData.length === 0) return;

    setDownloading(true);

    try {
      // Prepare data for Excel with proper formatting
      const excelData = filteredData.map((row) => ({
        'Row ID': row.errorRowId,
        'Document Number': row.externalDocumentNumber || '',
        'Item ID': row.itemId || '',
        'Item S.No': row.itemSno || '',
        'Quantity': row.quantity || 0,
        'Unit Price': row.unitPrice || 0,
        'Discount Price': row.discountprice || 0,
        'Error ID': row.errorId || '',
        'Error Description': row.errorDesc || '',
        'Created Date': row.createdDate ? row.createdDate : '',
      }));

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      worksheet['!cols'] = [
        { wch: 10 },  // Row ID
        { wch: 20 },  // Document Number
        { wch: 15 },  // Item ID
        { wch: 12 },  // Item S.No
        { wch: 10 },  // Quantity
        { wch: 15 },  // Unit Price
        { wch: 15 },  // Discount Price
        { wch: 12 },  // Error ID
        { wch: 40 },  // Error Description
        { wch: 15 },  // Created Date
      ];

      // Apply number formatting to currency columns
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      for (let R = range.s.r + 1; R <= range.e.r; ++R) {
        // Unit Price column (F)
        const unitPriceCell = XLSX.utils.encode_cell({ r: R, c: 5 });
        if (worksheet[unitPriceCell]) {
          worksheet[unitPriceCell].z = '₹#,##0.00';
        }
        
        // Discount Price column (G)
        const discountPriceCell = XLSX.utils.encode_cell({ r: R, c: 6 });
        if (worksheet[discountPriceCell]) {
          worksheet[discountPriceCell].z = '₹#,##0.00';
        }

        // Date column (J)
        const dateCell = XLSX.utils.encode_cell({ r: R, c: 9 });
        if (worksheet[dateCell] && worksheet[dateCell].v) {
          worksheet[dateCell].t = 'd';
          worksheet[dateCell].z = 'dd/mm/yyyy';
        }
      }

      // Create workbook and add worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Item Errors');

      // Generate filename with current date
      const today = new Date().toISOString().split('T')[0];
      const filename = `Amazon_BC_Item_Errors_${today}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);

      // Show success feedback
      setTimeout(() => {
        setDownloading(false);
      }, 500);

    } catch (err) {
      console.error('Error generating Excel file:', err);
      alert('Failed to generate Excel file. Please try again.');
      setDownloading(false);
    }
  };

  /* ---------- Sorting & Pagination ---------- */
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const aVal = a.externalDocumentNumber || '';
      const bVal = b.externalDocumentNumber || '';
      return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [filteredData, sortOrder]);

  const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedData.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedData, currentPage]);

  const formatDate = (value) => value ? new Date(value).toLocaleDateString('en-GB') : '-';
  const formatCurrency = (value) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value || 0);
  const unique = (key) => [...new Set(data.map((x) => x[key]).filter(Boolean))];

  return (
    <div className="amazon-bc-inv-item-error-screen">
      <div className="error-report-container">
        <div className="table-header">
          <div className="header-left">
            <h2>Amazon BC Invoice Item Errors</h2>
            <span className="record-count">Showing {filteredData.length} records</span>
          </div>
          <button 
            className="download-excel-button" 
            onClick={handleDownloadExcel}
            disabled={filteredData.length === 0 || downloading}
          >
            {downloading ? '⏳ Downloading...' : '📥 Download Excel'}
          </button>
        </div>

        <div className="filter-bar">
          <select value={selectedCreatedDate} onChange={(e) => setSelectedCreatedDate(e.target.value)}>
            <option value="">Created Date</option>
            {unique('createdDate').map((d) => (
              <option key={d} value={new Date(d).toISOString().slice(0, 10)}>{formatDate(d)}</option>
            ))}
          </select>

          <select value={selectedErrorId} onChange={(e) => setSelectedErrorId(e.target.value)}>
            <option value="">Error ID</option>
            {unique('errorId').map((id) => <option key={id} value={id}>{id}</option>)}
          </select>

          <select value={selectedExDocNo} onChange={(e) => setSelectedExDocNo(e.target.value)}>
            <option value="">External Document No</option>
            {unique('externalDocumentNumber').map((id) => <option key={id} value={id}>{id}</option>)}
          </select>

          <button className="clear-filter-button" onClick={clearFilters} disabled={!selectedCreatedDate && !selectedErrorId && !selectedExDocNo}>
            Clear Filters
          </button>
        </div>

        <div className="table-wrapper">
          <table className="amazon-bc-inv-item-error-table">
            <thead>
              <tr>
                <th>Row ID</th>
                <th className="sortable" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>
                  Document Number {sortOrder === 'asc' ? '↑' : '↓'}
                </th>
                <th>Created Date</th>
                {/* <th>Item S.No</th> */}
                <th className="center-header">Quantity</th>
                <th className="right-header">Unit Price</th>
                <th className="right-header">Discount Price</th>
                <th>Error ID</th>
                <th>Error Description</th>
                <th>Item ID</th>
              </tr>
            </thead>
            <tbody>
              {error && !loading ? (
                <tr>
                  <td colSpan="10" className="amazon-bc-inv-item-error-screen-error-cell">
                    <div className="amazon-bc-inv-item-error-screen-error-card">
                      <span className="amazon-bc-inv-item-error-screen-error-icon">⚠️</span>
                      <div className="amazon-bc-inv-item-error-screen-error-content">
                        <h3>Error Loading Data</h3>
                        <p>{error}</p>
                      </div>
                      <button className="retry-button" onClick={fetchErrorData}>Retry</button>
                    </div>
                  </td>
                </tr>
              ) : (loading || filtering) ? (
                <tr>
                  <td colSpan="10" className="amazon-bc-inv-item-error-screen-loader">
                    <div className="amazon-bc-inv-item-error-screen-loader-card">
                      <span className="amazon-bc-inv-item-error-screen-loader-icon">📦</span>
                      <span className="amazon-bc-inv-item-error-screen-loader-text">Loading items…</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="10" className="amazon-bc-inv-item-error-screen-empty-cell">
                    <div className="amazon-bc-inv-item-error-screen-empty-card">
                      <span className="amazon-bc-inv-item-error-screen-empty-icon">📭</span>
                      <div className="amazon-bc-inv-item-error-screen-empty-content">
                        <h3>No Records Found</h3>
                        <p>Try adjusting your filters or check back later.</p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedData.map((row) => (
                  <tr key={row.errorRowId}>
                    <td>{row.errorRowId}</td>
                    <td className="document-number">{row.externalDocumentNumber}</td>
                    <td className="date">{formatDate(row.createdDate)}</td>
                    {/* <td>{row.itemSno}</td> */}
                    <td className="quantity">{row.quantity}</td>
                    <td className="price">{formatCurrency(row.unitPrice)}</td>
                    <td className="price">{formatCurrency(row.discountprice)}</td>
                    <td>{row.errorId}</td>
                    <td className="error-desc">
                      {(() => {
                        const parsed = parseErrorDesc(row.errorDesc);
                        
                        if (typeof parsed === 'object' && parsed.code && parsed.message) {
                          return (
                            <div className="error-desc-content">
                              <span className="error-desc-code">{parsed.code}</span>
                              <span className="error-desc-message">{parsed.message}</span>
                            </div>
                          );
                        }
                        
                        return parsed;
                      })()}
                    </td>
                    <td>{row.itemId}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Amazon_BC_inv_item_errorScreen;